/**
 * ============================================================================
 * FourBall_ScoringManager
 * ============================================================================
 *
 * PURPOSE
 * -------
 * Calculates Four-Ball (Better Ball) match scores. This class is completely
 * stateless. It does not maintain the current state of the match as scores are
 * entered. Instead, whenever a score is requested it recalculates the match
 * from the first hole through the requested hole.
 *
 * This design guarantees that the displayed score is always derived from the
 * current scorecard data and never from cached or accumulated state.
 *
 *
 * RESPONSIBILITIES
 * ----------------
 * - Determine the Four-Ball score after any hole.
 * - Apply handicap strokes using FourBall_HandicapManager.
 * - Determine each team's best net score for every completed hole.
 * - Award holes to the winning team.
 * - Detect:
 *      • All Square
 *      • Team n Up
 *      • Dormie
 *      • Closed match (e.g. 3 & 2)
 * - Produce score displays for:
 *      • Scorecard header
 *      • Scorecard cells
 *      • Index page
 *
 *
 * NOT RESPONSIBLE FOR
 * -------------------
 * - Team selection dialogs
 * - Header click handling
 * - Saving team selections
 * - Handicap calculations
 * - Coloring scorecard cells
 * - Any user interface behavior
 *
 * Those responsibilities belong to FourBall_GameManager and
 * FourBall_HandicapManager.
 *
 *
 * DEPENDENCIES
 * ------------
 * FourBall_ScoringManager depends on:
 *
 *   • IRoundContext
 *       Course, players, pars, handicap indexes, etc.
 *
 *   • IGameContext
 *       Player definitions used by the game manager.
 *
 *   • FourBall_HandicapManager
 *       Determines which players or teams receive strokes on each hole.
 *
 *   • IFourBallTeams
 *       Defines the team pairings.
 *
 *
 * DESIGN NOTES
 * ------------
 * The public interface intentionally mirrors MatchPlay_GameManager so that
 * FourBall_GameManager simply delegates scoring requests here. All Four-Ball
 * scoring knowledge should remain inside this class.
 *
 *
 * PUBLIC METHODS
 * --------------
 *
 * constructor(...)
 *      Creates a scoring manager for one Four-Ball match.
 *
 * getScore(...)
 *      Returns the requested score display.
 *
 *      Supported display types:
 *          "scorecard-game-header"
 *          "scorecard-game-cell"
 *          "index-page"
 *
 *      The method recalculates the match from Hole 1 through the requested
 *      hole every time it is called.
 *
 *      Parameters:
 *          displayType     Requested display format.
 *          holeIndex       Hole to score through.
 *          playerScores    Gross scores for all players.
 *          currentPlayerId Used for player-perspective displays such as the
 *                          index page.
 *
 *      Returns:
 *          string | string[]
 *          depending on the requested display type, matching the contract used
 *          by MatchPlay_GameManager.
 *
 */
/**
 * Stateless 4-Ball scoring service.
 *
 * Responsibilities:
 * - Calculate the 4-Ball match score as of a requested hole.
 * - Ask FourBall_HandicapManager for strokes; do not duplicate handicap setup logic.
 * - Format results for the same display modes used by MatchPlay_GameManager.getScore().
 *
 * Non-responsibilities:
 * - Team setup UI.
 * - Saving teams to the scorecard.
 * - Scorecard cell coloring.
 * - Storing cumulative scoring state.
 */
export class FourBall_ScoringManager {
    constructor(rc, gc, handicapManager, teams) {
        this.rc = rc;
        this.gc = gc;
        this.handicapManager = handicapManager;
        this.teams = [
            { id: "team1", playerIds: [...teams.team1] },
            { id: "team2", playerIds: [...teams.team2] },
        ];
    }
    getGameSummary(holeIndex, playerScores) {
        const matchScore = this.getMatchScore(playerScores, holeIndex);
        if (!matchScore) {
            return undefined;
        }
        const team1 = matchScore.teams.find(team => team.teamId === "team1");
        const team2 = matchScore.teams.find(team => team.teamId === "team2");
        if (!team1 || !team2) {
            return undefined;
        }
        return {
            holeNumber: holeIndex + 1,
            holesRemaining: matchScore.holesRemaining,
            team1: {
                playerNames: team1.playerIds.map(id => this.getPlayerName(id)),
                holesAhead: team1.holesAhead,
            },
            team2: {
                playerNames: team2.playerIds.map(id => this.getPlayerName(id)),
                holesAhead: team2.holesAhead,
            },
        };
    }
    getPlayerName(playerId) {
        return (this.rc.roster?.find(player => player.id === playerId)?.nickName
            ?? playerId);
    }
    isMatchOver(holeIndex, playerScores) {
        const match = this.getMatchScore(playerScores, holeIndex);
        return !!match?.suppressScorecardDisplay;
    }
    /**
     * Same public scoring contract as MatchPlay_GameManager.getScore().
     */
    getScore(displayType, holeIndex = 0, playerScores, currentPlayerId) {
        const matchScore = this.getMatchScore(playerScores, holeIndex);
        if (displayType === "index-page") {
            if (!currentPlayerId)
                return "";
            if (!this.includesPlayer(currentPlayerId))
                return "";
            if (!matchScore)
                return "";
            return this.formatMatchScoreForPlayer(matchScore, currentPlayerId, displayType);
        }
        const formattedCellScore = matchScore
            ? this.formatForScorecardCell(matchScore)
            : "";
        switch (displayType) {
            case "scorecard-game-cell":
                return [formattedCellScore];
            case "scorecard-game-header":
                return [this.gc.header || "4 Ball", formattedCellScore];
            default:
                return "";
        }
    }
    includesPlayer(playerId) {
        return this.teams.some(team => team.playerIds.includes(playerId));
    }
    /**
 * Returns the CSS class used to visually mark which team won the hole.
 *
 * Returns:
 *   "fb-team1-win" when Team 1 won the hole.
 *   "fb-team2-win" when Team 2 won the hole.
 *   ""             when the hole is tied, incomplete, or unavailable.
 */
    getHoleWinnerCssClass(holeIndex, playerScores) {
        if (!playerScores)
            return "";
        if (this.teams.length !== 2)
            return "";
        const team1HoleScore = this.getBestNetScoreForTeam(this.teams[0], playerScores, holeIndex);
        const team2HoleScore = this.getBestNetScoreForTeam(this.teams[1], playerScores, holeIndex);
        if (team1HoleScore === undefined || team2HoleScore === undefined) {
            return "";
        }
        if (team1HoleScore < team2HoleScore) {
            return "fb-team1-win";
        }
        if (team2HoleScore < team1HoleScore) {
            return "fb-team2-win";
        }
        return "";
    }
    getTeamCssClass(teamNumber) {
        return teamNumber === 1
            ? "fb-team1"
            : "fb-team2";
    }
    getMatchScore(playerScores, holeIndex) {
        if (!playerScores)
            return undefined;
        if (this.teams.length !== 2)
            return undefined;
        if (this.teams.some(team => team.playerIds.length !== 2))
            return undefined;
        const totalHoles = this.rc.holesInRound ?? 18;
        let team1HolesAhead = 0;
        let closedMatchScore;
        let closedOnHoleIndex;
        for (let i = 0; i <= holeIndex; i++) {
            const team1HoleScore = this.getBestNetScoreForTeam(this.teams[0], playerScores, i);
            const team2HoleScore = this.getBestNetScoreForTeam(this.teams[1], playerScores, i);
            // Skipped/incomplete holes do not affect the match score.
            if (team1HoleScore === undefined || team2HoleScore === undefined)
                continue;
            if (team1HoleScore < team2HoleScore) {
                team1HolesAhead += 1;
            }
            else if (team2HoleScore < team1HoleScore) {
                team1HolesAhead -= 1;
            }
            const holesRemaining = Math.max(0, totalHoles - i - 1);
            if (!closedMatchScore && Math.abs(team1HolesAhead) > holesRemaining) {
                closedMatchScore = this.buildMatchScore(team1HolesAhead, holesRemaining);
                closedOnHoleIndex = i;
            }
        }
        if (closedMatchScore) {
            return {
                ...closedMatchScore,
                suppressScorecardDisplay: closedOnHoleIndex !== holeIndex,
            };
        }
        const currentTeam1Score = this.getBestNetScoreForTeam(this.teams[0], playerScores, holeIndex);
        const currentTeam2Score = this.getBestNetScoreForTeam(this.teams[1], playerScores, holeIndex);
        // Like MatchPlay cells: an open match is blank unless this hole is scored.
        if (currentTeam1Score === undefined || currentTeam2Score === undefined)
            return undefined;
        return this.buildMatchScore(team1HolesAhead, Math.max(0, totalHoles - holeIndex - 1));
    }
    getBestNetScoreForTeam(team, playerScores, holeIndex) {
        let bestNet;
        for (const playerId of team.playerIds) {
            const score = playerScores.find(p => p.playerId === playerId);
            const gross = score?.gross?.[holeIndex];
            if (!gross)
                continue;
            const net = gross - this.getPlayerStrokesOnHole(playerId, holeIndex);
            if (bestNet === undefined || net < bestNet) {
                bestNet = net;
            }
        }
        return bestNet;
    }
    buildMatchScore(team1HolesAhead, holesRemaining) {
        const team1 = this.teams[0];
        const team2 = this.teams[1];
        return {
            players: [
                ...team1.playerIds.map(playerId => ({ playerId, holesAhead: team1HolesAhead })),
                ...team2.playerIds.map(playerId => ({ playerId, holesAhead: -team1HolesAhead })),
            ],
            teams: [
                { teamId: "team1", playerIds: team1.playerIds, holesAhead: team1HolesAhead },
                { teamId: "team2", playerIds: team2.playerIds, holesAhead: -team1HolesAhead },
            ],
            holesRemaining,
        };
    }
    formatForScorecardCell(matchScore) {
        if (matchScore.suppressScorecardDisplay)
            return "";
        const leader = matchScore.teams.find(team => team.holesAhead > 0);
        if (!leader) {
            return matchScore.holesRemaining === 0 ? "All Square" : "AS";
        }
        const amount = leader.holesAhead;
        const holesRemaining = matchScore.holesRemaining;
        const leaderDisplay = this.getTeamDisplayCode(leader.playerIds);
        if (amount > holesRemaining) {
            return `${leaderDisplay} ${amount} & ${holesRemaining}`;
        }
        if (amount === holesRemaining) {
            return `${leaderDisplay} Dormie`;
        }
        return `${leaderDisplay} ${amount} UP`;
    }
    formatMatchScoreForPlayer(matchScore, currentPlayerId, displayType) {
        const currentTeam = matchScore.teams.find(team => team.playerIds.includes(currentPlayerId));
        const opponentTeam = matchScore.teams.find(team => !team.playerIds.includes(currentPlayerId));
        if (!currentTeam || !opponentTeam)
            return "";
        const amount = Math.abs(currentTeam.holesAhead);
        const holesRemaining = matchScore.holesRemaining;
        const isTied = currentTeam.holesAhead === 0;
        const currentTeamIsWinning = currentTeam.holesAhead > 0;
        const matchIsDormie = amount === holesRemaining;
        const matchIsFinal = amount > holesRemaining;
        const opponentDisplay = this.getTeamDisplayCode(opponentTeam.playerIds);
        if (isTied) {
            return `${opponentDisplay}as`;
        }
        if (matchIsFinal) {
            return `${currentTeamIsWinning ? "W" : "L"}${amount}&${holesRemaining}${opponentDisplay}`;
        }
        if (matchIsDormie) {
            return `Dormie ${currentTeamIsWinning ? "↑" : "↓"}${opponentDisplay}`;
        }
        return `${amount}${currentTeamIsWinning ? "↑" : "↓"}${opponentDisplay}`;
    }
    getPlayerStrokesOnHole(playerId, holeIndex) {
        const handicapManager = this.handicapManager;
        if (typeof handicapManager.getPlayerStrokesOnHole === "function") {
            return Number(handicapManager.getPlayerStrokesOnHole(playerId, holeIndex) ?? 0);
        }
        if (typeof handicapManager.getStrokesForPlayerOnHole === "function") {
            return Number(handicapManager.getStrokesForPlayerOnHole(playerId, holeIndex) ?? 0);
        }
        if (typeof handicapManager.getStrokesOnHole === "function") {
            return Number(handicapManager.getStrokesOnHole(playerId, holeIndex) ?? 0);
        }
        if (typeof handicapManager.getPlayerStrokeCount === "function") {
            return Number(handicapManager.getPlayerStrokeCount(playerId, holeIndex) ?? 0);
        }
        return 0;
    }
    getTeamDisplayCode(playerIds) {
        return playerIds.map(playerId => this.getDisplayCode(playerId)).join("");
    }
    getDisplayCode(playerId) {
        return this.gc.players.find(p => p.id === playerId)?.displayCode
            ?? this.rc.roster?.find(p => p.id === playerId)?.displayCode
            ?? this.rc.roster?.find(p => p.id === playerId)?.nickName
            ?? playerId;
    }
}
//# sourceMappingURL=FourBall_ScoringManager.js.map